import { supabase } from '../lib/supabase';
import type { 
  Project, 
  ProjectCandidate, 
  JobDescription, 
  TalentSearch, 
  DashboardEvent, 
  DashboardStats,
  MarketIntelligenceCache,
  ProjectWithDetails 
} from '../types/database';

export class DashboardService {
  // ===== DASHBOARD STATS =====
  
  static async getDashboardStats(organizationId: string): Promise<DashboardStats | null> {
    const { data, error } = await supabase
      .rpc('get_dashboard_stats', { org_id: organizationId });
    
    if (error) {
      console.error('Error fetching dashboard stats:', error);
      return null;
    }
    
    return data?.[0] || null;
  }

  // ===== PROJECTS MANAGEMENT =====
  
  static async getProjects(organizationId: string): Promise<ProjectWithDetails[]> {
    const { data: projects, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_candidates(count)
      `)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching projects:', error);
      return [];
    }
    
    return projects || [];
  }

  static async createProject(projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .insert(projectData)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error creating project:', error);
      return null;
    }
    
    // Record dashboard event
    await this.recordDashboardEvent(
      projectData.user_id,
      projectData.organization_id,
      'project_created',
      `New project created: ${projectData.title}`,
      `Project "${projectData.title}" has been created`,
      { project_id: data.id }
    );
    
    return data;
  }

  static async updateProject(projectId: string, updates: Partial<Project>): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', projectId)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error updating project:', error);
      return null;
    }
    
    return data;
  }

  static async deleteProject(projectId: string): Promise<boolean> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);
    
    if (error) {
      console.error('Error deleting project:', error);
      return false;
    }
    
    return true;
  }

  // ===== PROJECT CANDIDATES =====
  
  static async getProjectCandidates(projectId: string): Promise<ProjectCandidate[]> {
    const { data, error } = await supabase
      .from('project_candidates')
      .select(`
        *,
        candidates(id, name, email),
        analysis(id, fit_score)
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching project candidates:', error);
      return [];
    }
    
    return data || [];
  }

  static async addCandidateToProject(
    projectId: string, 
    candidateId: string, 
    analysisId?: string
  ): Promise<ProjectCandidate | null> {
    const { data, error } = await supabase
      .from('project_candidates')
      .insert({
        project_id: projectId,
        candidate_id: candidateId,
        analysis_id: analysisId,
        source: 'manual'
      })
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error adding candidate to project:', error);
      return null;
    }
    
    return data;
  }

  static async updateCandidateStatus(
    projectCandidateId: string,
    status: ProjectCandidate['status'],
    stage?: ProjectCandidate['stage']
  ): Promise<boolean> {
    const updates: Partial<ProjectCandidate> = { 
      status, 
      updated_at: new Date().toISOString() 
    };
    
    if (stage) {
      updates.stage = stage;
    }
    
    const { error } = await supabase
      .from('project_candidates')
      .update(updates)
      .eq('id', projectCandidateId);
    
    if (error) {
      console.error('Error updating candidate status:', error);
      return false;
    }
    
    return true;
  }

  // ===== JOB DESCRIPTIONS =====
  
  static async getJobDescriptions(organizationId: string): Promise<JobDescription[]> {
    const { data, error } = await supabase
      .from('job_descriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching job descriptions:', error);
      return [];
    }
    
    return data || [];
  }

  static async saveJobDescription(
    jobDescriptionData: Omit<JobDescription, 'id' | 'created_at' | 'updated_at'>
  ): Promise<JobDescription | null> {
    const { data, error } = await supabase
      .from('job_descriptions')
      .insert(jobDescriptionData)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error saving job description:', error);
      return null;
    }
    
    return data;
  }

  static async updateJobDescription(
    jobDescriptionId: string, 
    updates: Partial<JobDescription>
  ): Promise<JobDescription | null> {
    const { data, error } = await supabase
      .from('job_descriptions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', jobDescriptionId)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error updating job description:', error);
      return null;
    }
    
    return data;
  }

  // ===== JOB DESCRIPTIONS GENERATED =====
  
  static async getJobDescriptionsGenerated(organizationId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('job_descriptions_generated')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching generated job descriptions:', error);
      return [];
    }
    
    return data || [];
  }

  static async saveJobDescriptionGenerated(
    jobDescriptionData: {
      user_id: string;
      organization_id: string;
      title: string;
      content: string;
      company?: string;
      department?: string;
      location?: string;
      industry?: string;
      salary_range?: string;
      experience_level?: string;
      requirements?: string[];
      responsibilities?: string[];
      qualifications?: string[];
      benefits?: string[];
      tags?: string[];
      template_data?: Record<string, any>;
      generation_settings?: Record<string, any>;
      ai_prompt_used?: string;
      auto_fill_suggestions?: Record<string, any>;
      audience_targets?: string[];
      audience_optimization?: Record<string, any>;
      validation_result?: Record<string, any>;
      bias_detection_result?: Record<string, any>;
      is_published?: boolean;
      status?: string;
    }
  ): Promise<any | null> {
    const { data, error } = await supabase
      .from('job_descriptions_generated')
      .insert(jobDescriptionData)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error saving generated job description:', error);
      
      // Provide more detailed error information
      if (error.code === '23502') {
        throw new Error(`Missing required field: ${error.message}`);
      } else if (error.code === '23514') {
        throw new Error(`Data validation error: ${error.message}`);
      } else if (error.code === '23503') {
        throw new Error(`Invalid reference: ${error.message}`);
      } else if (error.code === '42501') {
        throw new Error('Permission denied. Please ensure you have proper organization access.');
      } else {
        throw new Error(`Database error: ${error.message || 'Unknown error occurred'}`);
      }
    }
    
    // Record dashboard event
    await this.recordDashboardEvent(
      jobDescriptionData.user_id,
      jobDescriptionData.organization_id,
      'job_created',
      `Job description generated: ${jobDescriptionData.title}`,
      `AI-generated job description for "${jobDescriptionData.title}" has been saved`,
      { job_description_id: data.id }
    );
    
    return data;
  }

  static async updateJobDescriptionGenerated(
    jobDescriptionId: string, 
    updates: {
      title?: string;
      content?: string;
      company?: string;
      department?: string;
      location?: string;
      industry?: string;
      salary_range?: string;
      experience_level?: string;
      requirements?: string[];
      responsibilities?: string[];
      qualifications?: string[];
      benefits?: string[];
      tags?: string[];
      template_data?: Record<string, any>;
      generation_settings?: Record<string, any>;
      ai_prompt_used?: string;
      auto_fill_suggestions?: Record<string, any>;
      audience_targets?: string[];
      audience_optimization?: Record<string, any>;
      validation_result?: Record<string, any>;
      bias_detection_result?: Record<string, any>;
      inclusivity_score?: number;
      audience_appeal_score?: number;
      overall_quality_score?: number;
      market_alignment_score?: number;
      is_published?: boolean;
      status?: string;
    }
  ): Promise<any | null> {
    const { data, error } = await supabase
      .from('job_descriptions_generated')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', jobDescriptionId)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error updating generated job description:', error);
      
      // Provide more detailed error information
      if (error.code === '23502') {
        throw new Error(`Missing required field: ${error.message}`);
      } else if (error.code === '23514') {
        throw new Error(`Data validation error: ${error.message}`);
      } else if (error.code === '23503') {
        throw new Error(`Invalid reference: ${error.message}`);
      } else if (error.code === '42501') {
        throw new Error('Permission denied. Please ensure you have proper organization access.');
      } else {
        throw new Error(`Database error: ${error.message || 'Unknown error occurred'}`);
      }
    }
    
    return data;
  }

  static async saveAudienceOptimization(
    jobDescriptionId: string,
    audienceTargets: string[],
    optimizationResult: Record<string, any>,
    appealScores: Record<string, number>
  ): Promise<boolean> {
    try {
      // Call the helper function from our schema
      const { error } = await supabase
        .rpc('save_audience_optimization', {
          job_desc_id: jobDescriptionId,
          target_audiences: audienceTargets,
          optimization_result: optimizationResult,
          appeal_scores: appealScores
        });

      if (error) {
        console.error('Error saving audience optimization:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error calling save_audience_optimization:', error);
      return false;
    }
  }

  static async updateJobDescriptionAnalytics(
    jobDescriptionId: string,
    actionType: 'view' | 'export' | 'edit' | 'copy' | 'pdf_download',
    timeSpent: number = 0
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .rpc('update_job_description_analytics', {
          job_desc_id: jobDescriptionId,
          action_type: actionType,
          time_spent: timeSpent
        });

      if (error) {
        console.error('Error updating job description analytics:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error calling update_job_description_analytics:', error);
      return false;
    }
  }

  static async getJobDescriptionWithStats(jobDescriptionId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_job_description_with_stats', {
          job_desc_id: jobDescriptionId
        });

      if (error) {
        console.error('Error getting job description with stats:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Error calling get_job_description_with_stats:', error);
      return null;
    }
  }

  static async getOrgJobDescriptionStats(organizationId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_org_job_description_stats', {
          org_id: organizationId
        });

      if (error) {
        console.error('Error getting org job description stats:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Error calling get_org_job_description_stats:', error);
      return null;
    }
  }

  // ===== TALENT SEARCHES =====
  
  static async saveTalentSearch(
    userId: string,
    organizationId: string,
    searchName: string,
    filters: Record<string, any>,
    resultsCount: number
  ): Promise<TalentSearch | null> {
    const { data, error } = await supabase
      .from('talent_searches')
      .insert({
        user_id: userId,
        organization_id: organizationId,
        search_name: searchName,
        filters,
        results_count: resultsCount,
        saved: true
      })
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error saving talent search:', error);
      return null;
    }
    
    return data;
  }

  static async getTalentSearches(organizationId: string): Promise<TalentSearch[]> {
    const { data, error } = await supabase
      .from('talent_searches')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('saved', true)
      .order('last_executed', { ascending: false });
    
    if (error) {
      console.error('Error fetching talent searches:', error);
      return [];
    }
    
    return data || [];
  }

  // ===== MARKET INTELLIGENCE CACHE =====
  
  static async getMarketIntelligenceData(
    organizationId: string,
    cacheKey: string
  ): Promise<MarketIntelligenceCache | null> {
    const { data, error } = await supabase
      .from('market_intelligence_cache')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('cache_key', cacheKey)
      .gt('expires_at', new Date().toISOString())
      .maybeSingle();
    
    if (error || !data) {
      return null;
    }
    
    return data;
  }

  static async cacheMarketIntelligenceData(
    organizationId: string,
    cacheKey: string,
    dataType: MarketIntelligenceCache['data_type'],
    data: Record<string, any>,
    industry?: string,
    location?: string,
    role?: string
  ): Promise<boolean> {
    const { error } = await supabase
      .from('market_intelligence_cache')
      .upsert({
        organization_id: organizationId,
        cache_key: cacheKey,
        data_type: dataType,
        industry,
        location,
        role,
        data,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      });
    
    if (error) {
      console.error('Error caching market intelligence data:', error);
      return false;
    }
    
    return true;
  }

  // ===== DASHBOARD EVENTS =====
  
  static async recordDashboardEvent(
    userId: string,
    organizationId: string,
    eventType: DashboardEvent['event_type'],
    title: string,
    description?: string,
    data?: Record<string, any>
  ): Promise<DashboardEvent | null> {
    const { data: eventData, error } = await supabase
      .rpc('record_dashboard_event', {
        user_id_param: userId,
        org_id: organizationId,
        event_type_param: eventType,
        title_param: title,
        description_param: description,
        data_param: data || {}
      });
    
    if (error) {
      console.error('Error recording dashboard event:', error);
      return null;
    }
    
    return eventData;
  }

  static async getDashboardEvents(userId: string, limit: number = 10): Promise<DashboardEvent[]> {
    const { data, error } = await supabase
      .from('dashboard_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching dashboard events:', error);
      return [];
    }
    
    return data || [];
  }

  static async markEventAsRead(eventId: string): Promise<boolean> {
    const { error } = await supabase
      .from('dashboard_events')
      .update({ read: true })
      .eq('id', eventId);
    
    if (error) {
      console.error('Error marking event as read:', error);
      return false;
    }
    
    return true;
  }

  // ===== PERFORMANCE METRICS =====
  
  static async recordPerformanceMetric(
    organizationId: string,
    metricType: 'analysis_speed' | 'user_activity' | 'system_load' | 'feature_usage',
    metricName: string,
    value: number,
    metadata?: Record<string, any>
  ): Promise<boolean> {
    const { error } = await supabase
      .from('performance_metrics')
      .insert({
        organization_id: organizationId,
        metric_type: metricType,
        metric_name: metricName,
        value,
        metadata: metadata || {}
      });
    
    if (error) {
      console.error('Error recording performance metric:', error);
      return false;
    }
    
    return true;
  }

  static async getPerformanceMetrics(
    organizationId: string,
    metricType?: string,
    hours: number = 24
  ): Promise<any[]> {
    let query = supabase
      .from('performance_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('recorded_at', new Date(Date.now() - hours * 60 * 60 * 1000).toISOString())
      .order('recorded_at', { ascending: false });
    
    if (metricType) {
      query = query.eq('metric_type', metricType);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching performance metrics:', error);
      return [];
    }
    
    return data || [];
  }
} 